import { isSeatCount } from "../../services/events/seat-payment-guards";

/**
 * №245 (решение Сергея 26.10.2026, вопрос 4 карточки `M2.5`). Сколько мест
 * события можно занять — зеркало серверного правила записи.
 *
 * Сервер отдаёт `effectiveCapacity` — меньшее из заявленной вместимости и
 * купленных мест (`effective-capacity.util.ts` в BE). Запись решает «место или
 * очередь» именно по нему, поэтому и человеку показывается оно: «осталось 30
 * мест» при 10 купленных обещало бы места, которых запись не даст.
 *
 * Поля нет в ответе (бэкенд старше №245, TD-31(б)) — показывается заявленная
 * вместимость, как до №245: так экран не падает и не выдумывает число.
 * Нечитаемое значение считается отсутствующим.
 *
 * 0 — мест нет, запись ставит в очередь (M2.1, решение №152). Прежнее чтение
 * «0 = без лимита» сервер снял 07.10.2026; экран, оставшийся на нём, звал бы
 * «Записаться» туда, где запись ставит в лист ожидания.
 *
 * Зеркало только подсказывает экрану, какую кнопку и какой остаток показать;
 * решает сервер — исход записи экраны берут из его ответа.
 */
export type CapacityFields = {
  readonly effectiveCapacity?: number | null | undefined;
  readonly maxParticipants?: number | null | undefined;
  readonly currentParticipants?: number | null | undefined;
};

/** Сколько мест можно занять; `null` — вместимость не известна. */
export const bookableCapacity = (event: CapacityFields): number | null => {
  if (isSeatCount(event.effectiveCapacity)) return event.effectiveCapacity;
  if (isSeatCount(event.maxParticipants)) return event.maxParticipants;
  return null;
};

/**
 * Сколько мест осталось; `null` — вместимость не известна. Не меньше нуля:
 * если занятых больше, чем мест можно занять, мест нет, а не «минус пять».
 */
export const seatsLeft = (event: CapacityFields): number | null => {
  const capacity = bookableCapacity(event);
  if (capacity === null) return null;
  const taken = isSeatCount(event.currentParticipants)
    ? event.currentParticipants
    : 0;
  return Math.max(capacity - taken, 0);
};

/**
 * Мест нет — запись поставит в очередь. Неизвестная вместимость «полно» не
 * значит: отнять у человека «Записаться» по отсутствию данных значило бы
 * выдумать отказ.
 */
export const isFullyBooked = (event: CapacityFields): boolean =>
  seatsLeft(event) === 0;
